import { FastField, getIn, useFormikContext } from "formik";
import React, { useState } from "react";
import { useAppSelector } from "../../../Store";
import { allMessagesStore } from "../../../Store/Slices/socket/AllMessagesSlice";
import {
  StyledInputField,
  StyledWidgetInputBox,
} from "../../Styles/StyledForms";
import AppErrorMessage from "./AppErrorMessage";
import AppSendSubmitButton from "./AppSendSubmitButton";
import { StaticTimePicker } from '@mui/x-date-pickers/StaticTimePicker';
import { Button, Box } from '@mui/material';
import Drawer from '@mui/material/Drawer';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import styled from "styled-components";
import AppSmallButton from "../UtilsComponents/AppSmallButton";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { theme } from "../../../Customization/Theme";

interface Props {
  name?: string;
  label?: string;
  children?: React.ReactNode;
  [otherProps: string]: any;
}

const AppFlowTimePicker: React.FC<Props> = ({
  name = "time",
  label = "Select Time",
  children,
  ...otherProps
}) => {
  const { errors, touched, values, handleChange, setFieldError, setFieldValue, setFieldTouched } = useFormikContext<any>();
  const { typingStatus, lastNodeResponse } = useAppSelector(allMessagesStore);
  const [open, setOpen] = useState(false);
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  
  const { min, max, isCurrentDayEnabled } = getCurrentDayConstraints(lastNodeResponse);
  const showDuration = lastNodeResponse?.time?.showDuration || !!lastNodeResponse?.time?.duration || false;
  
  // Use customDuration if available, otherwise fall back to the duration string
  const hasCustomDuration = lastNodeResponse?.time?.duration === "custom" && 
                           lastNodeResponse?.time?.customDuration !== undefined;
  const durationString = hasCustomDuration 
    ? `${lastNodeResponse.time.customDuration.time} ${lastNodeResponse.time.customDuration.unit}`
    : (lastNodeResponse?.time?.duration || "15 mins");
  
  // Parse duration from string (e.g., "15 mins" -> 15)
  const getDurationMinutes = (): number => {
    if (hasCustomDuration) {
      const { time, unit } = lastNodeResponse.time.customDuration;
      // If time is not provided, extract it from the duration string
      if (time === undefined) {
        const match = durationString.match(/(\d+)/);
        const extractedTime = match ? parseInt(match[1], 10) : 15;
        return unit === 'mins' ? extractedTime : extractedTime * 60;
      }
      // Convert to minutes based on unit
      return unit === 'mins' ? time : time * 60; // Assuming other unit might be 'hours'
    }
    
    const match = durationString.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : 15; // Default to 15 minutes if parsing fails
  };
  
  // Calculate end time based on selected time and duration
  const calculateEndTime = (startTime: Date): Date => {
    const endTime = new Date(startTime);
    endTime.setMinutes(endTime.getMinutes() + getDurationMinutes());
    return endTime;
  };
  
  // Format time range for display
  const formatTimeRange = (startTime: Date): string => {
    const endTime = calculateEndTime(startTime);
    const startFormatted = formatTimeFor12HourDisplay(`${String(startTime.getHours()).padStart(2, '0')}:${String(startTime.getMinutes()).padStart(2, '0')}`);
    const endFormatted = formatTimeFor12HourDisplay(`${String(endTime.getHours()).padStart(2, '0')}:${String(endTime.getMinutes()).padStart(2, '0')}`);
    return `${startFormatted} - ${endFormatted}`;
  };
  
  // Validate time is within allowed range
  const validateTimeSelection = (time: string) => {
    if (!isCurrentDayEnabled) {
      setFieldError(name, "Time selection is not available for today");
      return false;
    }

    if (!time) {
      setFieldError(name, "Please select a time");
      return false;
    }
    
    if (!min || !max) return true;
    
    // Compare as strings (HH:MM format)
    if (time < min) {
      setFieldError(name, `Time must be after ${min}`);
      return false;
    }
    
    if (time > max) {
      setFieldError(name, `Time must be before ${max}`);
      return false;
    }
    
    // Check if selected time is before current time
    // const currentDate = new Date();
    // const currentHours = String(currentDate.getHours()).padStart(2, '0');
    // const currentMinutes = String(currentDate.getMinutes()).padStart(2, '0');
    // const currentTimeStr = `${currentHours}:${currentMinutes}`;
    
    // if (time < currentTimeStr) {
    //   setFieldError(name, "Cannot select a time before current time");
    //   return false;
    // }
    
    // Check if end time (after adding duration) exceeds max time
    if (showDuration && max) {
      const [hours, minutes] = time.split(':').map(Number);
      const startTime = new Date();
      startTime.setHours(hours, minutes, 0);
      
      const endTime = calculateEndTime(startTime);
      const endTimeHours = String(endTime.getHours()).padStart(2, '0');
      const endTimeMinutes = String(endTime.getMinutes()).padStart(2, '0');
      const endTimeStr = `${endTimeHours}:${endTimeMinutes}`;
      
      // Special handling for midnight (00:00)
      const isEndTimeMidnight = endTimeHours === '00' && endTimeMinutes === '00';
      const isMaxMidnight = max === '00:00';
      
      // If max is midnight, it represents the end of day (24:00)
      // If endTime is midnight, it's the start of next day
      if ((endTimeStr > max && !isMaxMidnight) || (isEndTimeMidnight && !isMaxMidnight)) {
        const formattedEndTime = formatTimeFor12HourDisplay(endTimeStr);
        setFieldError(name, `Exceeds time (ends ${formattedEndTime})`);
        return false;
      }
    }
    
    return true;
  };

  const handleOpenPicker = () => {
    if (!isCurrentDayEnabled) {
      setFieldError(name, "Time selection is not available for today");
      return;
    }

    // Only initialize the time if selectedTime is null (first open)
    // This way we preserve the previously selected time when reopening
    if (!selectedTime) {
      // If there's already a time value, use it to initialize the picker
      if (getIn(values, name)) {
        // Check if the value is in 12-hour format (contains AM/PM)
        if (getIn(values, name).includes('AM') || getIn(values, name).includes('PM')) {
          // Parse 12-hour format
          const timeStr = getIn(values, name);
          const isPM = timeStr.includes('PM');
          const timeParts = timeStr.replace(/(AM|PM)/, '').trim().split(':');
          let hours = parseInt(timeParts[0], 10);
          const minutes = parseInt(timeParts[1], 10);
          
          // Convert to 24-hour format
          if (isPM && hours < 12) hours += 12;
          if (!isPM && hours === 12) hours = 0;
          
          const initialTime = new Date();
          initialTime.setHours(hours, minutes, 0);
          setSelectedTime(initialTime);
        } else {
          // Handle 24-hour format
          const [hours, minutes] = getIn(values, name).split(':').map(Number);
          const initialTime = new Date();
          initialTime.setHours(hours, minutes, 0);
          setSelectedTime(initialTime);
        }
      } else if (min) {
        // If no time is selected but there's a min time, initialize to that
        const [hours, minutes] = min.split(':').map(Number);
        const initialTime = new Date();
        initialTime.setHours(hours, minutes, 0);
        setSelectedTime(initialTime);
      } else {
        // Otherwise use current time
        setSelectedTime(new Date());
      }
    }
    setOpen(true);
  };

  const handleClosePicker = () => {
    // If there was no previously submitted time, reset the selectedTime
    if (!getIn(values, name)) {
      setSelectedTime(null);
    }
    setOpen(false);
  };

  const handleTimeChange = (newTime: Date | null) => {
    setSelectedTime(newTime);
  };

  // Format time for display in 12-hour format
  const formatTimeFor12HourDisplay = (time24hr: string): string => {
    if (!time24hr) return "";
    
    const [hours, minutes] = time24hr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM
    
    return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  // Convert 24-hour format to 12-hour format for submission
  const convertTo12HourFormat = (time24hr: string): string => {
    if (!time24hr) return "";
    
    const [hours, minutes] = time24hr.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12; // Convert 0 to 12 for 12 AM
    
    return `${hours12}:${String(minutes).padStart(2, '0')} ${period}`;
  };

  const handleTimeSubmit = () => {
    // Mark field as touched to show validation errors
    setFieldTouched(name, true);
    
    if (!isCurrentDayEnabled) {
      setFieldError(name, "Time selection is not available for today");
      handleClosePicker();
      return;
    }
    
    if (selectedTime) {
      // Store in 24-hour format internally for validation
      const time24hr = `${String(selectedTime.getHours()).padStart(2, '0')}:${String(selectedTime.getMinutes()).padStart(2, '0')}`;
      
      // Validate using 24-hour format
      const isValid = validateTimeSelection(time24hr);
      if (isValid) {
        // Convert to 12-hour format for submission
        const time12hr = convertTo12HourFormat(time24hr);
        
        // Calculate end time for both display and submission
        const endTime = calculateEndTime(selectedTime);
        const endTime24hr = `${String(endTime.getHours()).padStart(2, '0')}:${String(endTime.getMinutes()).padStart(2, '0')}`;
        const endTime12hr = convertTo12HourFormat(endTime24hr);
        
        // Always set the individual time values
        setFieldValue(name, time12hr);
        setFieldValue(`${name}End`, endTime12hr);
        
        // Always set the combined value for display and submission
        const timeRange = `${time12hr} - ${endTime12hr}`;
        setFieldValue(`${name}Range`, timeRange);
      }
      
      // Always close the drawer, even if validation fails
      handleClosePicker();
    } else {
      setFieldError(name, "Please select a time");
      // Close the drawer even when no time is selected
      handleClosePicker();
    }
  };

  // Handle time input change (when manually entered)
  const handleTimeInputChange = (e: any) => {
    if (!isCurrentDayEnabled) {
      setFieldError(name, "Time selection is not available for today");
      return;
    }

    const time24hr = e.target.value;
    handleChange(e); // Update the Formik state with original event
    
    // If valid, convert to 12-hour format
    if (validateTimeSelection(time24hr)) {
      const time12hr = convertTo12HourFormat(time24hr);
      setFieldValue(name, time12hr);
    }
  };

  // Create proper Date objects for min and max times
  const minTimeDate = min ? (() => {
    const [hours, minutes] = min.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  })() : undefined;

  const maxTimeDate = max ? (() => {
    const [hours, minutes] = max.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return date;
  })() : undefined;

  // If current day is not enabled, show appropriate message as label
  const displayLabel = !isCurrentDayEnabled 
    ? "Time selection not available today" 
    : (getIn(values, name) 
      ? (getIn(values, name).includes('AM') || getIn(values, name).includes('PM') 
          ? getIn(values, name) 
          : formatTimeFor12HourDisplay(getIn(values, name)))
      : label);

  return (
    <StyledWidgetInputBox
      disabled={typingStatus ? true : false}
      error={getIn(errors, name) && getIn(touched, name)}
    >
      
      <InputWrapper>
        <FastField name={name}>
          {({ field }: any) => (
            <StyledInputField
              {...otherProps}
              {...field}
              type="time"
              value={getIn(values, name) || ""}
              onChange={handleTimeInputChange}
              min={min}
              max={max}
              autoFocus={false}
              placeholder={label}
              disabled={!isCurrentDayEnabled || typingStatus}
              sx={{ display: 'none' }} // Hide the actual input field
            />
          )}
        </FastField>
        <AppSmallButton
          style={{
            width: '100%',
            height: '44px',
            maxHeight: '42px',
          }}
          onClick={handleOpenPicker}
          disabled={!isCurrentDayEnabled || typingStatus}
          variant="primaryoutlinebutton"
        >
          {(selectedTime || getIn(values, name)) && isCurrentDayEnabled
            ? (showDuration 
                ? (getIn(values, 'timeRange') || (selectedTime && formatTimeRange(selectedTime)))
                : (getIn(values, name) || (selectedTime && formatTimeFor12HourDisplay(`${String(selectedTime.getHours()).padStart(2, '0')}:${String(selectedTime.getMinutes()).padStart(2, '0')}`))))
            : (!isCurrentDayEnabled 
                ? "Time selection not available today" 
                : "Please select a time")}
          <AccessTimeIcon style={{ fontSize: '14px', marginLeft: '8px' }} />
        </AppSmallButton>
      </InputWrapper>
      <AppSendSubmitButton disabled={!isCurrentDayEnabled} />

      <AppErrorMessage
        error={getIn(errors, name)}
        visible={getIn(touched, name)}
      />

      {open && (
        <TimePickerOverlay>
          <TimePickerModal>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <StaticTimePicker
                value={selectedTime}
                onChange={handleTimeChange}
                minTime={minTimeDate}
                maxTime={maxTimeDate}
                slotProps={{
                  actionBar: { actions: [] }, // This removes the default action buttons
                }}
                // ampm={false} to make it 24 hr format
              />
              {showDuration && selectedTime && (
                <DurationInfo>
                  <span>Duration: {durationString}</span>
                  <span>End time: {formatTimeFor12HourDisplay(`${String(calculateEndTime(selectedTime).getHours()).padStart(2, '0')}:${String(calculateEndTime(selectedTime).getMinutes()).padStart(2, '0')}`)}</span>
                </DurationInfo>
              )}
              <ButtonContainer>
                <AppSmallButton variant="grey" onClick={handleClosePicker} sx={{ mr: 1 }}>
                  Cancel
                </AppSmallButton>
                <AppSmallButton onClick={handleTimeSubmit} variant="primary">
                  Save
                </AppSmallButton>
              </ButtonContainer>
            </LocalizationProvider>
          </TimePickerModal>
        </TimePickerOverlay>
      )}
    </StyledWidgetInputBox>
  );
};

export default AppFlowTimePicker;



// Get current day of week and time constraints
export const getCurrentDayConstraints = (lastNodeResponse: any) => {
  if (!lastNodeResponse?.time?.weekdays) return { min: "", max: "", isCurrentDayEnabled: false };
  
  const days = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
  const currentDayIndex = new Date().getDay(); // 0 = Sunday, 1 = Monday, etc.
  const currentDay = days[currentDayIndex];
  
  // Find the current day config regardless of enabled status
  const currentDayConfig = lastNodeResponse.time.weekdays.find(
    (day: any) => day.day === currentDay
  );
  
  // Check if current day is enabled
  const isCurrentDayEnabled = currentDayConfig?.enabled || false;
  
  // Only return time constraints if the day is enabled
  if (!isCurrentDayEnabled) return { min: "", max: "", isCurrentDayEnabled };
  
  // Get the day config with enabled status
  const dayConfig = lastNodeResponse.time.weekdays.find(
    (day: any) => day.day === currentDay && day.enabled
  );
  
  if (!dayConfig) return { min: "", max: "", isCurrentDayEnabled };
  
  // Convert ISO times to HH:MM format for the time input
  const fromTime = new Date(dayConfig.from);
  const toTime = new Date(dayConfig.to);
  
  const formatTimeForInput = (date: Date) => {
    return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
  };
  
  return {
    min: formatTimeForInput(fromTime),
    max: formatTimeForInput(toTime),
    isCurrentDayEnabled
  };
};

const InputWrapper = styled(Box)({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  input: {
    backgroundColor: 'red !important',
    display: 'none !important',
  }
});

// New styled components for the CSS modal approach
const TimePickerOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-end;
  z-index: 1300;
  // border: 1px solid red,
`;

const TimePickerModal = styled.div`
  background-color: white;
  border-radius: 8px 8px 0 0;
  width: 100%;
  // max-width: 600px;
  // max-height: 80vh;
  max-height: calc(100vh - 8px);
  height: calc(100vh - 8px); 
  overflow: hidden;
  box-shadow: 0px -2px 10px rgba(0, 0, 0, 0.1);
  animation: slideUp 0.3s ease-out;
  max-width: calc(100% - 8px);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  margin-bottom: 4px;

  .MuiPickersLayout-root {
    height: 100% !important;
    align-items: center !important;
  }
  
  @keyframes slideUp {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }
`;

const ButtonContainer = styled.div`
border-top: 1px solid ${theme.palette.default.grey};
  display: flex;
  justify-content: flex-end;
  padding: 16px;
`;

// Add a new styled component for the duration info
const DurationInfo = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 8px 16px;
  background-color: ${theme.palette.default.lightGrey};
  color: ${theme.palette.default.darkGrey};
  font-size: 14px;
`;


